import {useMemo} from 'react'
import {useDispatch} from 'react-redux'
import {CSSTransition, TransitionGroup} from 'react-transition-group'
import Link from 'next/link'
import {map} from '@cullylarson/f'
import {
    useCart,
    useSelectedWarrantyInfo,
    useMakeOfferPrice,
    useIsMakeOffer,
    useWarrantyOptions,
    useSelectedWarrantyId,
    useItemPrice,
    useCartSummary,
} from '@@client/cart/cart-hooks'
import {getWarrantyCost} from '@@client/cart/cart-repo'
import {classnames} from '@@client/lib/classes'
import {endPlural} from '@@client/lib/util'
import {formatNumber, formatDollars} from '@@client/lib/numbers'
import {preventDefault} from '@@client/lib/events'
import {useChat, useConfig} from '@@client/lib/hooks'
import {getPartSummary} from '@@client/parts/parts-helper'
import {removeCartItem, makeOfferItem, selectItemWarranty} from '@@client/cart/actions'
import {labelStyle, subHeadingStyle, valueStyle} from '@@client/parts/styles'
import Layout from '@@client/app/Layout'
import {SubLayoutGraySidebar, Sidebar, Main} from '@@client/app/SubLayoutGraySidebar'
import Col100 from '@@client/components/Col100'
import CoreChargeMessage from '@@client/checkout/CoreChargeMessage'
import PartPhotoFeatured from '@@client/parts/PartPhotoFeatured'
import FormPrice from '@@client/components/FormPrice'
import HeaderSubHeaderBlueUnderlineAndSeparator from '@@client/components/HeaderSubHeaderBlueUnderlineAndSeparator'
import FormSelect from '@@client/components/FormSelect'
import makeOfferValidate from '@@client/cart/make-offer-validate'

const SelectWarranty = ({
    partInfo,
    isMakeOffer,
    selectedWarrantyInfo,
    selectedWarrantyId,
    onChange,
}) => {
    const config = useConfig()
    const cart = useCart()
    const itemPrice = useItemPrice(partInfo.inventoryId, cart)
    const optionTitleFromInfo = info => {
        const warrantyCost = getWarrantyCost(info, itemPrice)

        return `${info.titleAbbrev} – ${warrantyCost ? formatDollars(warrantyCost) : 'Free'}`
    }

    const warrantyOptions = useWarrantyOptions()
    const warrantySelectOptions = useMemo(() => Object.values(map((info, id) => ({value: id, label: optionTitleFromInfo(info)}), warrantyOptions)), [itemPrice, warrantyOptions])
    const summary = getPartSummary(partInfo)

    if(config.inventory.noWarrantyPartTypes.includes(partInfo.partType)) {
        return (<p><em>This part does not come with a warranty.</em></p>)
    }

    const handleChange = warrantyId => {
        onChange(partInfo.inventoryId, warrantyId)
    }

    if(isMakeOffer) {
        return (
            <>
                {
                    !selectedWarrantyInfo
                        ? null
                        : (
                            <div className='adendum'><em>* Make-offer parts come with our {selectedWarrantyInfo.titleLong}</em></div>
                        )
                }
                <style jsx>
                    {`
                        @import '@css/variables.css';

                        .adendum {
                            font-size: $size--smaller;
                        }
                    `}
                </style>
            </>
        )
    }

    const selectElId = `select-warranty-${partInfo.inventoryId}`

    return (
        <div className='container'>
            <label htmlFor={selectElId} className={labelStyle.className}>Select Warranty</label>
            <form onSubmit={preventDefault()}>
                <FormSelect
                    id={selectElId}
                    value={selectedWarrantyId}
                    options={warrantySelectOptions}
                    noOptionsMessage='No warranty options available.'
                    onChange={handleChange}
                    isClearable={false}
                    aria-label={`Select warranty for item: ${summary}`}
                />
            </form>
            <style jsx>
                {`
                    form :global(.field) {
                        margin: 0;
                    }
                `}
            </style>
        </div>
    )
}

const WarrantyPrice = ({partInfo}) => {
    const selectedWarrantyInfo = useSelectedWarrantyInfo(partInfo.inventoryId)
    const isMakeOffer = useIsMakeOffer(partInfo.inventoryId)
    const itemPrice = useItemPrice(partInfo.inventoryId)
    const warrantyCost = getWarrantyCost(selectedWarrantyInfo, itemPrice)

    return (
        <>
            <div className='container'>
                <div className={labelStyle.className}>Warranty Price</div>
                <div className={classnames('price', valueStyle.className)}>{formatDollars(warrantyCost)}{isMakeOffer ? <span>*</span> : null}</div>
            </div>
            {labelStyle.styles}
            {valueStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .price {
                        font-size: $size--larger;
                    }
                `}
            </style>
        </>
    )
}

const CallUs = () => {
    const config = useConfig()

    return (
        <>
            <div className='container'>
                <div className={labelStyle.className}>Call</div>
                <div><a className={subHeadingStyle.className} href={`tel:${config.contact.phoneFull}`}>{config.contact.phoneHuman}</a></div>
            </div>
            {labelStyle.styles}
            {subHeadingStyle.styles}
        </>
    )
}

const ChatUs = () => {
    const chat = useChat()

    if(!chat.isAvailable) return null

    return (
        <>
            <div className='container'>
                <div className={labelStyle.className}>Chat</div>
                <div><button className={classnames(subHeadingStyle.className, 'm--no-btn', 'm--clickable')}>Live Chat</button></div>
            </div>
            {labelStyle.styles}
            {subHeadingStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        padding-left: 12px;
                        margin-left: 12px;
                        border-left: 1px solid $c--blue--darker;
                    }

                    button {
                        &:hover {
                            color: $c--link--body--hover;
                        }
                    }
                `}
            </style>
        </>
    )
}

const Contact = () => {
    return (
        <>
            <div className='container a-md'>
                <h3>Questions / Concerns</h3>
                <div className='content'>
                    <CallUs />
                    <ChatUs />
                </div>
            </div>
            {subHeadingStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        margin-top: 20px;
                    }

                    h3 {
                        text-transform: uppercase;
                        color: white;
                        background: $c--gradient--blue--dark;
                        font-size: $size--regular;
                        margin: 0;
                        padding: 5px 18px;
                    }

                    .content {
                        display: flex;
                        background: white;
                        padding: 14px 18px;
                        border: 1px solid $c--gray--medium;
                        border-top: 0;
                        border-radius: 0 0 6px 6px;
                    }
                `}
            </style>
        </>
    )
}

const ItemPrice = ({
    partInfo,
    isMakeOffer,
    makeOfferPrice,
    offerErrors,
    onOfferChange, // does not validate the offer amount
}) => {
    makeOfferPrice = makeOfferPrice || ''

    const id = 'make-offer-' + partInfo.inventoryId
    const summary = getPartSummary(partInfo)

    const handleOfferChange = (e) => {
        const amount = e.target.value
        onOfferChange(partInfo.inventoryId, amount)
    }

    return (
        <>
            <div className='container'>
                {
                    isMakeOffer
                        ? (
                            <>
                                <form onSubmit={preventDefault()}>
                                    <FormPrice
                                        title='Make Offer Price'
                                        id={id}
                                        titleClassName={labelStyle.className}
                                        value={makeOfferPrice}
                                        onChange={handleOfferChange}
                                        errors={offerErrors}
                                        aria-label={`Make offer on item: ${summary}`}
                                    />
                                </form>
                            </>
                        )
                        : (
                            <>
                                <div className={labelStyle.className}>Price</div>
                                <div className={classnames('amount', valueStyle.className)}>{formatDollars(partInfo.retailPrice)}</div>
                            </>
                        )
                }
                {
                    !partInfo.corePrice
                        ? null
                        : (
                            <div className='core'>+ {formatDollars(partInfo.corePrice)} Core Deposit</div>
                        )
                }
            </div>
            {labelStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .amount {
                        font-size: $size--larger;
                        font-weight: $weight--bold;
                    }

                    .core {
                        font-style: italic;
                        font-size: $size--small;
                        margin-top: 4px;
                    }

                    .container :global(input) {
                        font-size: $size--medium;
                    }
                `}
            </style>
        </>
    )
}

const OneCartItem = ({
    partInfo,
    onRemove,
    onOfferChange,
    onWarrantyChange,
}) => {
    const isMakeOffer = useIsMakeOffer(partInfo.inventoryId)
    const makeOfferPrice = useMakeOfferPrice(partInfo.inventoryId)
    const selectedWarrantyInfo = useSelectedWarrantyInfo(partInfo.inventoryId)
    const selectedWarrantyId = useSelectedWarrantyId(partInfo.inventoryId)
    const offerErrors = isMakeOffer ? makeOfferValidate(makeOfferPrice).errors : []
    const hasCore = Boolean(partInfo.corePrice)
    const summary = getPartSummary(partInfo)

    return (
        <>
            <div className='part'>
                <div className='photo'>
                    <PartPhotoFeatured
                        partInfo={partInfo}
                        isNew={false}
                    />
                </div>
                <div className='details'>
                    <div className='line-one'>
                        <div className='item'>
                            <div className={labelStyle.className}>Part Category</div>
                            <div className={valueStyle.className}>{partInfo.partTypeName}</div>
                        </div>
                        <div className='item'>
                            <div className={labelStyle.className}>Vehicle Information</div>
                            <div className={valueStyle.className}>{partInfo.modelYear} {partInfo.makeName} {partInfo.carlineName}</div>
                        </div>
                    </div>
                    <div className='line-two'>
                        <div className='item'>
                            <div className={labelStyle.className}>Mileage</div>
                            <div className={valueStyle.className}>{partInfo.mileage ? formatNumber(partInfo.mileage) : <span>&mdash;</span>}</div>
                        </div>
                        <div className='item'>
                            <div className={labelStyle.className}>Stock #</div>
                            <div className={valueStyle.className}>{partInfo.stockTicketNumber || <span>&mdash;</span>}</div>
                        </div>
                        <div className='item location'>
                            <div className={labelStyle.className}>Location</div>
                            <div className={valueStyle.className}>{partInfo.storeName || <span>&mdash;</span>}</div>
                        </div>
                        <div className='select-warranty'>
                            <SelectWarranty
                                partInfo={partInfo}
                                isMakeOffer={isMakeOffer}
                                selectedWarrantyInfo={selectedWarrantyInfo}
                                selectedWarrantyId={selectedWarrantyId}
                                onChange={onWarrantyChange}
                            />
                        </div>
                    </div>
                </div>
                <div className='price-actions-warranty'>
                    <ItemPrice
                        partInfo={partInfo}
                        isMakeOffer={isMakeOffer}
                        makeOfferPrice={makeOfferPrice}
                        offerErrors={offerErrors}
                        onOfferChange={onOfferChange}
                    />
                    <div className={classnames('actions', hasCore ? 'has-core' : null)}>
                        <button aria-label={`Remove item from cart: ${summary}`} onClick={() => onRemove(partInfo.inventoryId)} className={classnames('m--no-btn', 'm--clickable', subHeadingStyle.className)}>Remove</button>
                    </div>
                    <WarrantyPrice partInfo={partInfo} />
                </div>
            </div>
            {labelStyle.styles}
            {valueStyle.styles}
            {subHeadingStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .part {
                        display: grid;
                        grid-template-columns: 1fr 175px;
                        margin-bottom: 15px;
                        color: $c--body--text;
                        text-decoration: none;
                        font-weight: $weight--regular;
                        border: 1px solid $c--gray--medium;

                        @media(--xxs) { grid-template-columns: 1fr 130px; }
                        @media(--lg) { grid-template-columns: 200px 1fr 150px; }
                        @media(--xl) { grid-template-columns: 200px 1fr 200px; }
                    }

                    .photo {
                        order: 2;

                        @media(--lg) {
                            order: unset;
                        }
                    }

                    .details {
                        padding: 17px;
                        background: $c--gray--bg;
                        order: 1;
                        grid-row: auto / span 2;

                        @media(--lg) {
                            display: flex;
                            flex-direction: column;
                            justify-content: space-between;
                            order: unset;
                            grid-row: auto;
                        }

                        .line-one {
                            margin-bottom: 10px;

                            @media(--lg) {
                                display: grid;
                                grid-auto-flow: column;
                                grid-gap: 17px;
                            }

                            .item {
                                @media(--md-max) {
                                    border-bottom: 1px solid $c--blue--light-gray;
                                    margin-bottom: 10px;
                                    padding-bottom: 10px;
                                }
                            }
                        }

                        .line-two {
                            @media(--lg) {
                                display: grid;
                                grid-template-columns: 1fr 1fr;
                                grid-gap: 17px;
                            }

                            @media(--xl) {
                                grid-auto-flow: column;
                                grid-template-columns: 1fr 1fr 2fr;
                            }

                            .item {
                                @media(--md-max) {
                                    display: grid;
                                    grid-auto-flow: column;
                                    grid-template-columns: 75px 1fr;
                                    grid-gap: 10px;
                                    margin-bottom: 10px;
                                }
                            }

                            .location {
                                @media(--md-max) {
                                    border-bottom: 1px solid $c--blue--light-gray;
                                    padding-bottom: 10px;
                                }

                                @media(--lg) {
                                    display: none;
                                }
                            }

                            .select-warranty {
                                @media(--lg-only) {
                                    grid-column: 1 / 3;
                                }
                            }
                        }
                    }

                    .price-actions-warranty {
                        padding: 17px;
                        background: $c--blue--lightest;
                        order: 3;

                        @media(--lg) {
                            order: unset;
                        }
                    }

                    .actions {
                        border-bottom: 1px solid $c--blue--light-gray;
                        margin-top: 20px;
                        margin-bottom: 10px;
                        padding-bottom: 10px;

                        &.has-core {
                            margin-top: 5px;
                        }

                        button:hover {
                            color: $c--link--body--hover;
                        }
                    }
                `}
            </style>
        </>
    )
}

const CartItems = ({cart, onRemove, onOfferChange, onWarrantyChange}) => {
    const items = Object.values(cart.items)

    if(!items.length) return <p><em>There are no items in your cart.</em></p>

    return (
        <>
            <TransitionGroup className='cart-items'>
                {items.map(partInfo => {
                    return (
                        <CSSTransition
                            classNames='item-transition'
                            key={partInfo.inventoryId}
                            timeout={500}
                            onExit={(el) => {
                                el.style.height = el.scrollHeight + 'px'
                            }}
                            onExiting={(el) => {
                                el.style.height = 0
                            }}
                            onExited={(el) => {
                                el.style.height = null
                            }}
                        >
                            <div className='item'>
                                <OneCartItem
                                    partInfo={partInfo}
                                    onRemove={onRemove}
                                    onOfferChange={onOfferChange}
                                    onWarrantyChange={onWarrantyChange}
                                />
                            </div>
                        </CSSTransition>
                    )
                })}
            </TransitionGroup>
            <style jsx>
                {`
                    .item {
                        transition: all 500ms ease;
                        opacity: 1;
                    }

                    .item-transition-exit {
                        opacity: 0;
                    }
                `}
            </style>
        </>
    )
}

const CartSummary = ({cart}) => {
    const items = Object.values(cart.items)

    if(!items.length) return null

    const cartSummary = useCartSummary()

    return (
        <>
            <div className='container'>
                <h2 className={classnames('a-md', subHeadingStyle.className)}>Cart Summary</h2>
                <div className='content'>
                    <div className='item cart-items'>
                        <div className='label'>Cart Items:<br />({cartSummary.items.numItems} Item{endPlural(cartSummary.items.numItems)})</div>
                        <div className='line-total'>{formatDollars(cartSummary.items.total)}</div>
                    </div>
                    {
                        cartSummary.cores.numItems === 0
                            ? null
                            : (
                                <>
                                    <div className='item item-extra'>
                                        <div className='label'>Core Deposit:<br />({cartSummary.cores.numItems} Item{endPlural(cartSummary.cores.numItems)})</div>
                                        <div className='line-total'>{formatDollars(cartSummary.cores.total)}</div>
                                    </div>
                                    <div className='item item-extra'>
                                        <div className='label core-message'><CoreChargeMessage /></div>
                                    </div>
                                </>
                            )
                    }
                    {
                        cartSummary.extendedWarranties.numItems === 0
                            ? null
                            : (
                                <div className='item item-extra'>
                                    <div className='label'>Extended Warranty:<br />({cartSummary.extendedWarranties.numItems} Item{endPlural(cartSummary.extendedWarranties.numItems)})</div>
                                    <div className='line-total'>{formatDollars(cartSummary.extendedWarranties.total)}</div>
                                </div>
                            )
                    }
                    <div className='item grand-total'>
                        <div className='label'>Cart Total:</div>
                        <div className='line-total'>{formatDollars(cartSummary.grandTotal)}</div>
                    </div>
                    <p><Link href='/checkout'><a className='btn btn--yellow checkout'>Checkout</a></Link></p>
                    <div className='note'>Local sales tax, and shipping &amp; handling charges are calculated during checkout.</div>
                </div>
            </div>
            {subHeadingStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        @media(--sm-only) {
                            max-width: 380px;
                            margin-left: auto;
                            margin-right: auto;
                        }
                    }

                    h2 {
                        margin-bottom: 0;
                        margin-top: 6px; /* magic number to make the border show up at the same place as the border in the other column */
                        padding-bottom: 18px; /* magic number to make the border show up at the same place as the border in the other column */
                    }

                    .content {
                        padding: 14px 18px;
                        border: 1px solid $c--gray--medium;
                        border-top: 2px solid $c--blue--darker;
                        border-radius: 0 0 6px 6px;
                        background: white;
                    }

                    .label {
                        font-size: $size--small;
                    }

                    .core-message {
                        width: 100%;
                    }

                    .line-total {
                        font-size: $size--moderate;
                        font-weight: $weight--bold;
                        padding-left: 10px;
                    }

                    .item {
                        display: flex;
                        justify-content: space-between;
                    }

                    .item-extra {
                        margin-top: 6px;
                        padding-left: 8px;

                        .label {
                            font-style: italic;
                        }
                    }

                    .grand-total {
                        margin-top: 15px;
                        padding: 10px 0;
                        border-top: 1px solid $c--blue--light-gray;
                        border-bottom: 1px solid $c--blue--light-gray;
                    }

                    .note {
                        margin-top: 15px;
                        font-style: italic;
                        text-align: center;
                        font-size: $size--small;

                        @media(--md) {
                            font-size: $size--even-smaller;
                        }
                    }

                    a.btn {
                        width: 100%;
                    }
                `}
            </style>
        </>
    )
}

const getNumItemsHeading = (cart) => {
    const items = Object.values(cart.items)

    return `${items.length} item${endPlural(items)} in your cart`
}

export default function Page() {
    const dispatch = useDispatch()
    const cart = useCart()

    const handleRemove = (inventoryId) => {
        dispatch(removeCartItem(inventoryId))
    }

    const handleOfferChange = (inventoryId, amount) => {
        dispatch(makeOfferItem(inventoryId, amount))
    }

    const handleWarrantyChange = (inventoryId, warrantyId) => {
        dispatch(selectItemWarranty(inventoryId, warrantyId))
    }

    return (
        <Layout title='Cart'>
            <div className='mobile-heading t-sm'>
                <Col100>
                    <HeaderSubHeaderBlueUnderlineAndSeparator heading='Cart' subHeading={getNumItemsHeading(cart)} />
                </Col100>
            </div>
            <SubLayoutGraySidebar>
                <Main>
                    <HeaderSubHeaderBlueUnderlineAndSeparator className='a-md' heading='Cart' subHeading={getNumItemsHeading(cart)} />
                    <CartItems cart={cart} onRemove={handleRemove} onOfferChange={handleOfferChange} onWarrantyChange={handleWarrantyChange} />
                </Main>
                <Sidebar>
                    <CartSummary cart={cart} />
                    <Contact />
                </Sidebar>
            </SubLayoutGraySidebar>
            <div className='mobile-cart-summary t-sm'>
                <Col100>
                    <CartSummary cart={cart} />
                </Col100>
            </div>
            <style jsx>
                {`
                    .mobile-heading {
                        margin-top: 20px;
                    }

                    .mobile-cart-summary {
                        margin-bottom: 20px;
                    }
                `}
            </style>
        </Layout>
    )
}
